import { Component, EventEmitter, HostBinding, Input, Output } from '@angular/core';
import { AuthService } from '@services';
import { objectIsEmpty, RangeTableFilter, TableChip, TableFilterConfig } from '@shared';
import { LocalStorageService } from 'ngx-webstorage';

@Component({
  selector: 'app-filter-chip-list',
  templateUrl: './filter-chip-list.component.html',
  styleUrls: ['./filter-chip-list.component.scss'],
})
export class FilterChipListComponent {
  private _chips: TableChip[] = [];

  @Output() filtersApplied: EventEmitter<void> = new EventEmitter<void>();
  @Input() config: TableFilterConfig;
  @Input() showClearButton: boolean = true;
  @Input() isLoading: boolean = true;

  @Input()
  set chips(value: TableChip[]) {
    this._chips = value;
  }

  get chips() {
    return this._chips;
  }

  private get storageKey(): string {
    return `${this.authService.user.userLogin}_${this.config.storageKey}`;
  }

  constructor(
    private storage: LocalStorageService,
    private authService: AuthService) {
  }

  @HostBinding('class') get classes() {
    return (this.isLoading && !this.chips.length) || !this.chips.length ? 'hidden' : '';
  }

  getChipContent({ tableFilterConfig, value }: TableChip): string {
    return tableFilterConfig.chipData?.content(value);
  }

  getChipTooltip({ tableFilterConfig, value }: TableChip): string {
    return !tableFilterConfig.chipData?.tooltipDisabled?.(value)
      ? tableFilterConfig.chipData?.tooltipText?.(value)
      : '';
  }

  clearFilter({ groupName, tableFilterConfig, value }: TableChip): void {
    const filters = this.retrieveFiltersFromStorage();
    const controlName = tableFilterConfig.controlName;
    const isRangeFilter = this.isRangeValue(value);
    const filtersToCancel = tableFilterConfig.chipData?.filtersToCancel ?? [];

    const updatedFilters = {
        ...filters,
        [groupName]: {
          ...filters[groupName],
          [controlName]: isRangeFilter
            ? this.getRangeFilterUpdate(tableFilterConfig as RangeTableFilter)
            : tableFilterConfig.initialValue,
          ...filtersToCancel.reduce((acc, filter) => {
            return { ...acc, [filter.controlName]: filter.initialValue };
          }, {}),
        },
      }
    ;

    this.updateFiltersAndEmit(updatedFilters);
  }

  private isRangeValue(value: any): boolean {
    return typeof value === 'object' && value !== null && !(value instanceof Date);
  }

  private getRangeFilterUpdate(config: RangeTableFilter) {
    return {
      [config.fromFilter.controlName]: config.fromFilter.initialValue,
      [config.toFilter.controlName]: config.toFilter.initialValue,
    };
  }

  private updateFiltersAndEmit(updatedFilters: Record<string, any>): void {
    this.saveFiltersToStorage(updatedFilters);
    this.filtersApplied.emit();
  }

  clearFilters(): void {
    this.storage.clear(this.storageKey);
    this.filtersApplied.emit();
  }

  private saveFiltersToStorage(filters: Record<string, any>): void {
    this.storage.store(this.storageKey, objectIsEmpty(filters) ? {} : filters);
  }

  private retrieveFiltersFromStorage(): Record<string, any> {
    return this.storage.retrieve(this.storageKey) ?? {};
  }
}
